import { FC } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';

export interface HouseholdMemberOption {
  id: number;
  cert_member_id?: number;
  first_name: string;
  middle_name?: string | null;
  last_name: string;
}

interface HoHDemotionDialogProps {
  open: boolean;
  currentHohName: string;
  newRoleName: string;
  memberOptions: HouseholdMemberOption[];
  value: HouseholdMemberOption | null;
  onChange: (value: HouseholdMemberOption | null) => void;
  loading?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

const getFullName = (member: HouseholdMemberOption): string => {
  const parts = [
    member.first_name,
    member.middle_name,
    member.last_name,
  ].filter(Boolean);
  return parts.join(' ');
};

const HoHDemotionDialog: FC<HoHDemotionDialogProps> = ({
  open,
  currentHohName,
  newRoleName,
  memberOptions,
  value,
  onChange,
  loading = false,
  onConfirm,
  onClose,
}) => {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Transfer household ownership</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          You are about to change{' '}
          <strong style={{ textTransform: 'capitalize' }}>
            {currentHohName}
          </strong>
          's role to <strong>{newRoleName}</strong>.
          <br />
          <br />
          <strong>Important:</strong>
          <br />
          Once this transfer is complete, you will no longer be able to manage
          this household.
          <br />
          The new head of household will be able to edit, add and remove members
          to this household.
        </DialogContentText>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Select the new Head of Household:
        </Typography>
        <Autocomplete
          options={memberOptions}
          getOptionLabel={(option) => getFullName(option)}
          value={value}
          onChange={(_event, newValue) => onChange(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="New Head of Household"
              required
              size="small"
            />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="primary"
          disabled={loading || !value}
          startIcon={
            loading ? <CircularProgress size={20} color="inherit" /> : null
          }
        >
          <span>{loading ? 'Saving...' : 'Yes, Confirm Change'}</span>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HoHDemotionDialog;
